/**
 * Tony Mony wishlist: client-side only (localStorage), no backend/app.
 * Stores a lightweight snapshot of each saved product (id, handle, url,
 * title, image, price) at the moment it's added, so the wishlist page
 * can render without any extra network requests.
 */

import { CartLinesUpdateEvent } from '@shopify/events';

const STORAGE_KEY = 'tm-wishlist';
const CHANGE_EVENT = 'tm:wishlist:change';

// Same AED dirham icon used everywhere else prices render (see
// assets/icon-currency-dirham.svg / snippets/price.liquid), inlined here
// since the wishlist page's cards are built by JS, not Liquid.
const CURRENCY_ICON_SVG =
  '<svg class="tm-wishlist-card__price-icon" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">' +
  '<path d="M5.6 2.8v14.4h3.1c4.3 0 7.3-3.2 7.3-7.2s-3-7.2-7.3-7.2h-3.1z" stroke="currentColor" stroke-width="2.4" stroke-linejoin="round" stroke-linecap="round"/>' +
  '<path d="M2.6 7.9h12.8M2.6 11.3h12.8" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>' +
  '</svg>';

function readWishlist() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const items = raw ? JSON.parse(raw) : [];
    return Array.isArray(items) ? items : [];
  } catch (error) {
    return [];
  }
}

function writeWishlist(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (error) {
    // Storage unavailable (private browsing, quota, etc.) -- fail silently,
    // the UI simply won't persist across reloads.
  }
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items } }));
}

function isSaved(id) {
  return readWishlist().some((item) => item.id === id);
}

function toggleSaved(product) {
  const items = readWishlist();
  const index = items.findIndex((item) => item.id === product.id);
  if (index > -1) {
    items.splice(index, 1);
  } else {
    items.push(product);
  }
  writeWishlist(items);
  return index === -1;
}

function removeSaved(id) {
  writeWishlist(readWishlist().filter((item) => item.id !== id));
}

function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value == null ? '' : String(value);
  return div.innerHTML;
}

// Same canonical size ordering used sitewide for the PDP's size picker
// (snippets/variant-main-picker.liquid) -- keeps the wishlist page's size
// dropdown in S/M/L/XL order instead of whatever order the product's
// variants happen to be defined in.
const SIZE_ORDER = ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', '2xl', 'xxxl', '3xl', '4xl', '5xl'];

function sortSizeValues(values) {
  const ranked = [];
  const leftover = [...values];
  SIZE_ORDER.forEach((key) => {
    const index = leftover.findIndex((value) => value.toLowerCase().trim() === key);
    if (index > -1) {
      ranked.push(leftover.splice(index, 1)[0]);
    }
  });
  return ranked.concat(leftover);
}

// The /products/{handle}.js Ajax endpoint's top-level "options" field is
// documented as plain option-name strings, but some Shopify API versions
// (and options_with_values, when present) return {name, values, ...}
// objects instead -- read either shape so size/color detection doesn't
// silently break depending on which one a given store gets back.
function getOptionNames(product) {
  const source = Array.isArray(product.options_with_values) ? product.options_with_values : product.options;
  if (!Array.isArray(source)) return [];
  return source.map((entry) => (typeof entry === 'string' ? entry : entry && entry.name) || '');
}

const productJsonCache = new Map();

function fetchProductJson(handle) {
  if (!handle) return Promise.resolve(null);
  if (productJsonCache.has(handle)) return productJsonCache.get(handle);
  const promise = fetch(`/products/${handle}.js`, { credentials: 'same-origin' })
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null);
  productJsonCache.set(handle, promise);
  return promise;
}

function cartAddUrl() {
  return (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
}

function cartUrl() {
  return (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart';
}

function cartItemsComponentSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
    .filter((id) => Boolean(id));
}

// Prefer the same live re-fetch the rest of the theme uses (cached/
// de-duplicated by the component itself) over a plain GET, so this stays
// in step with any in-flight cart request instead of racing it.
function fetchFreshCart() {
  const cartItemsComponent = document.querySelector('cart-items-component');
  if (cartItemsComponent && typeof cartItemsComponent.fetchCartData === 'function') {
    return cartItemsComponent.fetchCartData();
  }
  return fetch(`${cartUrl()}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(
    (response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    }
  );
}

/**
 * Adds items to the real Shopify cart. This used to be a bare POST to
 * /cart/add.js that only patched the header's count bubble by hand
 * afterwards -- it never told the rest of the theme a cart change had
 * happened, so the cart drawer/page's own cart-items-component (which only
 * updates in response to CartLinesUpdateEvent, same as every native
 * add-to-cart button dispatches) kept showing whatever it had rendered at
 * page load until a full reload. That's the same reason it could look
 * like the line item's photo was missing: without requesting `sections`
 * and resolving this event, nothing ever handed it the freshly-rendered
 * markup (images included) for the new item.
 */
function addToCart(items) {
  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  const deferredEventPromise = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferredEventPromise.promise,
    })
  );

  return fetch(cartAddUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items, sections: cartItemsComponentSectionIds().join(',') }),
  })
    .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
    .then(({ ok, data }) =>
      fetchFreshCart()
        .then((cart) => {
          deferredEventPromise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              items: cart.items,
              source: 'tm-wishlist',
              sections: data.sections,
              itemCount: totalQuantity,
              didError: !ok,
            },
          });
        })
        .catch((error) => deferredEventPromise.reject(error))
        .then(() => ({ ok, data }))
    )
    .catch((error) => {
      deferredEventPromise.reject(error);
      throw error;
    });
}

/**
 * Wraps a heart button (PDP, product cards). Reads its product data from
 * data-* attributes on the element itself.
 */
class TmWishlistToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;
    this._onChange = () => this.updateState();
    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      this.handleClick();
    });
    document.addEventListener(CHANGE_EVENT, this._onChange);
    window.addEventListener('pageshow', this._onPageShow);
    this.updateState();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this._onChange);
    window.removeEventListener('pageshow', this._onPageShow);
  }

  // Horizon navigates between pages with cross-document view transitions,
  // which can restore a page straight from the browser's back/forward
  // cache instead of re-running connectedCallback -- so a heart toggled
  // on a different page (e.g. removed from the wishlist page) can leave
  // this element showing its old is-active state until something forces
  // a re-check. Same fix cart-icon.js already uses for the cart bubble.
  _onPageShow = (event) => {
    if (event.persisted) this.updateState();
  };

  handleClick() {
    const { productId, productHandle, productUrl, productTitle, productImage, productPrice, productVariantId } =
      this.dataset;
    if (!productId) return;
    const match = this.#currentVariantMatch();
    toggleSaved({
      id: productId,
      handle: productHandle || '',
      url: productUrl || '#',
      title: productTitle || '',
      // The matched variant's own image if it has one, same as how
      // Shopify's own cart line falls back to the product image when a
      // variant has none -- this is the one saved with the item, not
      // whatever image happened to be showing when the page loaded.
      image: (match && match.image) || productImage || '',
      price: productPrice || '',
      variantId: (match && String(match.id)) || this.#fallbackVariantId() || productVariantId || '',
    });
  }

  /**
   * On the PDP, both the URL's "variant" param and the product form's
   * hidden id input are updated by Horizon *asynchronously* -- picking a
   * color triggers a server round-trip that morphs the variant picker
   * with fresh markup, and only once that finishes do those update. A
   * heart click that lands before that finishes would still read the
   * previous variant with either approach (this was tried and did not
   * hold up). The same staleness applies to the block's own
   * data-product-image attribute -- it's a server-rendered snapshot of
   * whatever variant was selected at page load, so it doesn't follow a
   * client-side color swatch click either.
   *
   * The one thing that updates instantly, synchronously, with no
   * network wait at all, is which radio in the variant picker's own
   * fieldsets is :checked -- that's native browser behavior on click.
   * So instead of asking Horizon what's selected, read the checked
   * inputs directly (data-option-name identifies which option each
   * fieldset is, e.g. "Color"/"Size", regardless of their display
   * order -- see snippets/variant-main-picker.liquid, which
   * deliberately shows Color before Size even when Shopify's option1/
   * option2 order differs) and match that combination against this
   * product's own variants (data-variants / data-option-names on this
   * element, a static page-load snapshot from blocks/tm-product-wishlist.liquid,
   * which now carries each variant's own image alongside its options).
   * @returns {{ id: number, image: string | null } | null}
   */
  #currentVariantMatch() {
    const picker = document.querySelector(
      `variant-picker[data-product-id="${CSS.escape(this.dataset.productId || '')}"]`
    );
    const checkedRadios = picker ? Array.from(picker.querySelectorAll('input[type="radio"]:checked')) : [];
    if (checkedRadios.length === 0) return null;

    try {
      const optionNames = JSON.parse(this.dataset.optionNames || '[]');
      const variants = JSON.parse(this.dataset.variants || '[]');
      const selections = {};
      checkedRadios.forEach((radio) => {
        const name = radio.dataset.optionName;
        if (name) selections[name] = radio.value;
      });

      return (
        variants.find((variant) =>
          optionNames.every((name, index) => {
            const key = `option${index + 1}`;
            return selections[name] === undefined || selections[name] === variant[key];
          })
        ) || null
      );
    } catch (error) {
      // Malformed/missing embedded data -- fall through to the
      // less precise fallbacks below rather than breaking the add.
      return null;
    }
  }

  #fallbackVariantId() {
    const fromUrl = new URL(window.location.href).searchParams.get('variant');
    if (fromUrl) return fromUrl;

    const productId = this.dataset.productId;
    if (!productId) return '';
    const form = document.querySelector(`product-form-component[data-product-id="${CSS.escape(productId)}"]`);
    return form?.querySelector('input[name="id"]')?.value || '';
  }

  updateState() {
    const active = isSaved(this.dataset.productId);
    this.classList.toggle('is-active', active);
    if (this.button) {
      const { labelAdd, labelRemove } = this.dataset;
      this.button.setAttribute('aria-pressed', String(active));
      this.button.setAttribute(
        'aria-label',
        active ? labelRemove || 'Remove from wishlist' : labelAdd || 'Add to wishlist'
      );
    }
  }
}

/**
 * A small count badge, used in the header (desktop) and mobile drawer.
 * Hides itself (via [hidden]) whenever the count is 0.
 */
class TmWishlistCount extends HTMLElement {
  connectedCallback() {
    this._onChange = () => this.update();
    document.addEventListener(CHANGE_EVENT, this._onChange);
    window.addEventListener('pageshow', this._onPageShow);
    this.update();
  }

  // See TmWishlistToggle's _onPageShow -- a bfcache-restored page (header
  // icon, mobile drawer) never re-runs connectedCallback, so a count
  // added/removed on another page wouldn't otherwise be reflected here
  // until the next same-page change event.
  _onPageShow = (event) => {
    if (event.persisted) this.update();
  };

  disconnectedCallback() {
    window.removeEventListener('pageshow', this._onPageShow);
    document.removeEventListener(CHANGE_EVENT, this._onChange);
  }

  update() {
    const count = readWishlist().length;
    this.textContent = String(count);
    this.hidden = count === 0;
  }
}

/**
 * The wishlist page itself: renders saved items into [data-wishlist-grid]
 * as real, working product cards -- each with its own size selector and
 * "Add to Cart" button (hydrated from the live product JSON, so sizes and
 * availability are always current, not whatever they were when the item
 * was saved) -- plus toggles [data-wishlist-empty] when there are none.
 */
class TmWishlistPage extends HTMLElement {
  connectedCallback() {
    this.emptyEl = this.querySelector('[data-wishlist-empty]');
    this.gridEl = this.querySelector('[data-wishlist-grid]');
    this.clearButton = this.querySelector('[data-wishlist-clear]');
    this.addAllButton = this.querySelector('[data-wishlist-add-all]');

    this.clearButton?.addEventListener('click', () => {
      if (readWishlist().length === 0) return;
      if (window.confirm(this.dataset.i18nClearConfirm || 'Remove all items from your wishlist?')) {
        writeWishlist([]);
      }
    });

    this.addAllButton?.addEventListener('click', () => this.handleAddAll());

    this._onChange = () => this.render();
    document.addEventListener(CHANGE_EVENT, this._onChange);
    window.addEventListener('pageshow', this._onPageShow);
    this.render();
  }

  // See TmWishlistToggle's _onPageShow -- a bfcache-restored wishlist
  // page wouldn't otherwise pick up a removal/add made on another page.
  _onPageShow = (event) => {
    if (event.persisted) this.render();
  };

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this._onChange);
    window.removeEventListener('pageshow', this._onPageShow);
  }

  render() {
    if (!this.emptyEl || !this.gridEl) return;
    const items = readWishlist();

    if (this.clearButton) this.clearButton.hidden = items.length === 0;
    if (this.addAllButton) this.addAllButton.hidden = items.length === 0;

    if (items.length === 0) {
      this.emptyEl.hidden = false;
      this.gridEl.hidden = true;
      this.gridEl.innerHTML = '';
      return;
    }

    this.emptyEl.hidden = true;
    this.gridEl.hidden = false;
    const removeLabel = escapeHtml(this.dataset.i18nRemoveFromWishlist || 'Remove from wishlist');
    const loadingLabel = escapeHtml(this.dataset.i18nLoadingOptions || 'Loading options…');
    this.gridEl.innerHTML = items
      .map(
        (item) => `
          <div class="tm-wishlist-card" data-wishlist-card data-handle="${escapeHtml(item.handle)}" data-variant-id="${escapeHtml(item.variantId || '')}">
            <a href="${escapeHtml(item.url)}" class="tm-wishlist-card__media">
              ${
                item.image
                  ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy" width="400" height="500">`
                  : ''
              }
            </a>
            <button type="button" class="tm-wishlist-card__remove" data-remove="${escapeHtml(item.id)}" aria-label="${removeLabel}">&times;</button>
            <div class="tm-wishlist-card__info">
              <a href="${escapeHtml(item.url)}" class="tm-wishlist-card__title">${escapeHtml(item.title)}</a>
              ${item.price ? `<span class="tm-wishlist-card__price">${CURRENCY_ICON_SVG}${escapeHtml(item.price)}</span>` : ''}
              <div class="tm-wishlist-card__variant" data-variant-picker>
                <span class="tm-wishlist-card__variant-loading">${loadingLabel}</span>
              </div>
            </div>
          </div>
        `
      )
      .join('');

    this.gridEl.querySelectorAll('[data-remove]').forEach((button) => {
      button.addEventListener('click', () => removeSaved(button.dataset.remove));
    });

    this.gridEl.querySelectorAll('[data-wishlist-card]').forEach((card) => {
      this.hydrateCard(card);
    });
  }

  hydrateCard(card) {
    const handle = card.dataset.handle;
    const pickerEl = card.querySelector('[data-variant-picker]');
    if (!pickerEl) return;

    const {
      i18nAddToCart: addToCartLabel = 'Add to Cart',
      i18nSoldOut: soldOutLabel = 'Sold Out',
      i18nAdded: addedLabel = 'Added ✓',
      i18nTryAgain: tryAgainLabel = 'Try again',
      i18nSize: sizeLabel = 'Size',
    } = this.dataset;

    fetchProductJson(handle).then((product) => {
      if (!product || !Array.isArray(product.variants) || product.variants.length === 0) {
        pickerEl.innerHTML = '';
        return;
      }

      // This store's products are Color + Size (in that order), so find
      // whichever option is named "Size" and treat the other one as the
      // color, locked to whatever was selected on the PDP when the heart
      // was clicked (card.dataset.variantId) -- the wishlist page only
      // lets you change size, matching how it works on the PDP itself.
      const optionNames = getOptionNames(product);
      let sizeIndex = optionNames.findIndex((name) => /size/i.test(name));
      if (sizeIndex === -1) sizeIndex = optionNames.length - 1;
      const sizeKey = `option${sizeIndex + 1}`;

      const lockIndex = optionNames.length > 1 ? optionNames.findIndex((_name, i) => i !== sizeIndex) : -1;
      const lockKey = lockIndex > -1 ? `option${lockIndex + 1}` : null;

      const variantId = card.dataset.variantId;
      const matchedVariant =
        (variantId && product.variants.find((variant) => String(variant.id) === String(variantId))) || null;
      const lockValue = lockKey ? (matchedVariant || product.variants[0])[lockKey] : null;

      const relevantVariants = lockKey
        ? product.variants.filter((variant) => variant[lockKey] === lockValue)
        : product.variants;
      const values = sortSizeValues([...new Set(relevantVariants.map((variant) => variant[sizeKey]))]);
      const initialSize = (matchedVariant && matchedVariant[sizeKey]) || values[0];

      const lockLabelHtml = lockValue
        ? `<span class="tm-wishlist-card__color-label">${escapeHtml(lockValue)}</span>`
        : '';

      const selectId = `WishlistSize-${product.id}`;
      pickerEl.innerHTML = `
        ${lockLabelHtml}
        <select class="tm-wishlist-card__size" id="${selectId}" data-size-select aria-label="${escapeHtml(sizeLabel)}">
          ${values
            .map(
              (value) =>
                `<option value="${escapeHtml(value)}"${value === initialSize ? ' selected' : ''}>${escapeHtml(value)}</option>`
            )
            .join('')}
        </select>
        <button type="button" class="button tm-wishlist-card__add" data-add-to-cart>${escapeHtml(addToCartLabel)}</button>
      `;

      const selectEl = pickerEl.querySelector('[data-size-select]');
      const addButton = pickerEl.querySelector('[data-add-to-cart]');

      const findVariant = () =>
        product.variants.find(
          (variant) => variant[sizeKey] === selectEl.value && (!lockKey || variant[lockKey] === lockValue)
        );

      const syncAvailability = () => {
        const variant = findVariant();
        const available = Boolean(variant && variant.available);
        addButton.disabled = !available;
        addButton.textContent = available ? addToCartLabel : soldOutLabel;
      };

      selectEl.addEventListener('change', syncAvailability);
      syncAvailability();

      addButton.addEventListener('click', () => {
        const variant = findVariant();
        if (!variant || !variant.available) return;
        addButton.disabled = true;
        const originalText = addButton.textContent;
        addToCart([{ id: variant.id, quantity: 1 }])
          .then(({ ok }) => {
            addButton.textContent = ok ? addedLabel : tryAgainLabel;
          })
          .catch(() => {
            addButton.textContent = tryAgainLabel;
          })
          .finally(() => {
            setTimeout(() => {
              addButton.textContent = originalText;
              syncAvailability();
            }, 1800);
          });
      });
    });
  }

  handleAddAll() {
    const cards = Array.from(this.gridEl.querySelectorAll('[data-wishlist-card]'));
    const items = cards
      .map((card) => {
        const addButton = card.querySelector('[data-add-to-cart]:not(:disabled)');
        const selectEl = card.querySelector('[data-size-select]');
        if (!addButton || !selectEl) return null;
        return { card, size: selectEl.value };
      })
      .filter(Boolean);

    if (items.length === 0) return;

    // Re-resolve each card's currently selected variant at click time
    // (from the same cached product JSON the card already hydrated with).
    Promise.all(
      items.map(({ card, size }) =>
        fetchProductJson(card.dataset.handle).then((product) => {
          if (!product) return null;
          const optionNames = getOptionNames(product);
          let sizeIndex = optionNames.findIndex((name) => /size/i.test(name));
          if (sizeIndex === -1) sizeIndex = optionNames.length - 1;
          const sizeKey = `option${sizeIndex + 1}`;

          const lockIndex = optionNames.length > 1 ? optionNames.findIndex((_name, i) => i !== sizeIndex) : -1;
          const lockKey = lockIndex > -1 ? `option${lockIndex + 1}` : null;

          const variantId = card.dataset.variantId;
          const matchedVariant =
            (variantId && product.variants.find((v) => String(v.id) === String(variantId))) || null;
          const lockValue = lockKey ? (matchedVariant || product.variants[0])[lockKey] : null;

          const variant = product.variants.find(
            (candidate) =>
              candidate[sizeKey] === size && candidate.available && (!lockKey || candidate[lockKey] === lockValue)
          );
          return variant ? { id: variant.id, quantity: 1 } : null;
        })
      )
    ).then((resolved) => {
      const validItems = resolved.filter(Boolean);
      if (validItems.length === 0) return;
      this.addAllButton.disabled = true;
      const originalText = this.addAllButton.textContent;
      const addedToCartLabel = this.dataset.i18nAddedToCart || 'Added to cart ✓';
      const tryAgainLabel = this.dataset.i18nTryAgain || 'Try again';
      addToCart(validItems)
        .then(({ ok }) => {
          this.addAllButton.textContent = ok ? addedToCartLabel : tryAgainLabel;
        })
        .finally(() => {
          setTimeout(() => {
            this.addAllButton.disabled = false;
            this.addAllButton.textContent = originalText;
          }, 1800);
        });
    });
  }
}

if (!customElements.get('tm-wishlist-toggle')) {
  customElements.define('tm-wishlist-toggle', TmWishlistToggle);
}
if (!customElements.get('tm-wishlist-count')) {
  customElements.define('tm-wishlist-count', TmWishlistCount);
}
if (!customElements.get('tm-wishlist-page')) {
  customElements.define('tm-wishlist-page', TmWishlistPage);
}
